import {
  FormContext,
  FormContextProvider,
  FormSchemaFields,
} from '@stormwater-software/lib-form';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import { Modal } from '@stormwater-software/lib-ui';
import React from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import {
  AssetStatus,
  AssetType,
  AssetTypeField,
  assetStatusesApi,
  assetTypesApi,
} from '../../../../utils/api/asset';
import {
  AssetFieldAnswer,
  ProjectAsset,
  ProjectAssetInput,
  projectAssetsApi,
} from '../../../../utils/api/projectAsset';
import projectInspectionTemplatesApi from '../../../../utils/api/projectInspectionTemplates';
import validator from '../../../../utils/FormValidator';
import { getCurrentPosition } from '../../../../utils/googlemaps';

interface ICreateAssetModalProps {
  projectId: number;
  divisionId: number;
  /** When set, the modal edits this asset instead of creating a new one. */
  asset?: ProjectAsset | null;
  onSaved: () => void;
  onClose: () => void;
}

/** Prefix so dynamic asset-type field keys never collide with core fields. */
const FIELD_PREFIX = 'field_';

const optionsOf = (field: AssetTypeField) =>
  (field.metadata?.options || []).map((o) => ({ label: o, value: o }));

/** Build a FormSchema control for one static asset-type field. */
const controlForField = (field: AssetTypeField) => {
  const base = {
    label: field.is_required ? `${field.label} *` : field.label,
    validation: field.is_required ? { required: true } : undefined,
    style: {
      flexBasis: '50%',
      display: field.is_active === false ? 'none' : undefined,
    },
  };

  switch (field.field_type) {
    case 'number':
      return {
        ...base,
        controlType: UIControlType.number,
        placeholder: field.metadata?.unit
          ? `in ${field.metadata.unit}`
          : undefined,
      };
    case 'date':
      return { ...base, controlType: UIControlType.date };
    case 'select':
      return {
        ...base,
        controlType: UIControlType.select,
        options: optionsOf(field),
        valueKey: 'value',
        labelKey: 'label',
      };
    case 'yes_no':
      return {
        ...base,
        controlType: UIControlType.select,
        options: [
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
        ],
        valueKey: 'value',
        labelKey: 'label',
      };
    case 'yes_no_na':
      return {
        ...base,
        controlType: UIControlType.select,
        options: [
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
          { label: 'N/A', value: 'na' },
        ],
        valueKey: 'value',
        labelKey: 'label',
      };
    default:
      return base;
  }
};

const CreateAssetModal: React.FC<ICreateAssetModalProps> = ({
  projectId,
  divisionId,
  asset,
  onSaved,
  onClose,
}) => {
  const isEdit = !!asset;
  const [saving, setSaving] = React.useState(false);
  const typesQuery = useQuery(['assetTypes', divisionId], () =>
    assetTypesApi.index(divisionId),
  );
  const statusesQuery = useQuery(['assetStatuses', divisionId], () =>
    assetStatusesApi.index(divisionId),
  );
  const templatesQuery = useQuery(
    ['projectInspectionTemplates', projectId],
    () => projectInspectionTemplatesApi.index(projectId),
  );

  const types = typesQuery.data || [];
  const statuses = statusesQuery.data || [];
  const templates = templatesQuery.data?.data || [];

  const coreSchema = {
    asset_type_id: {
      label: 'Asset Type *',
      controlType: UIControlType.select,
      options: types.map((t: AssetType) => ({ label: t.name, value: t.id })),
      valueKey: 'value',
      labelKey: 'label',
      validation: { required: true },
      style: { flexBasis: '50%' },
    },
    asset_status_id: {
      label: 'Status *',
      controlType: UIControlType.select,
      options: statuses.map((s: AssetStatus) => ({
        label: s.name,
        value: s.id,
      })),
      valueKey: 'value',
      labelKey: 'label',
      validation: { required: true },
      style: { flexBasis: '50%' },
    },
    project_inspection_templates: {
      label: 'Inspection Types *',
      controlType: UIControlType.customTagSelect,
      options: templates.map((t) => ({
        id: t.id,
        name: t.inspection_template?.name || `Template ${t.id}`,
      })),
      valueKey: 'id',
      labelKey: 'name',
      isMulti: true,
      openOnFocus: true,
      showClearAll: true,
      placeholder: 'Select inspection types',
      validation: { required: true },
      style: { flexBasis: '100%' },
    },
    asset_name: {
      label: 'Asset ID *',
      controlType: UIControlType.number,
      validation: { required: true },
      style: { flexBasis: '50%' },
    },
  };

  const locationSchema = {
    city: { label: 'City', style: { flexBasis: '33%' } },
    state: { label: 'State', style: { flexBasis: '33%' } },
    county: { label: 'County', style: { flexBasis: '33%' } },
    latitude: {
      label: 'Latitude *',
      placeholder: 'Latitude',
      parse: validator.parseDecimal,
      validation: { required: true },
      style: { flexBasis: '50%' },
    },
    longitude: {
      label: 'Longitude *',
      placeholder: 'Longitude',
      parse: validator.parseDecimal,
      validation: { required: true },
      style: { flexBasis: '50%' },
    },
    notes: { label: 'Notes', controlType: UIControlType.textarea },
  };

  /** Static (non-inspectable, active) fields for the chosen asset type. */
  const staticFieldsFor = (assetTypeId?: number | string): AssetTypeField[] => {
    // Native <select> emits string values, so compare numerically.
    const id = Number(assetTypeId);
    const type = types.find((t: AssetType) => t.id === id);

    return (type?.fields || [])
      .filter((f) => f.is_active !== false && !f.is_inspectable)
      .sort((a, b) => a.display_order - b.display_order);
  };

  const handleSave = async (value: any) => {
    if (saving) return;
    setSaving(true);
    const fields = staticFieldsFor(value.asset_type_id);
    const field_answers: AssetFieldAnswer[] = fields
      .map((f) => ({
        asset_field_type_id: f.id!,
        value:
          value[`${FIELD_PREFIX}${f.id}`] != null
            ? String(value[`${FIELD_PREFIX}${f.id}`])
            : null,
      }))
      .filter((a) => a.value != null && a.value !== '');

    const payload: ProjectAssetInput = {
      asset_name: value.asset_name != null ? String(value.asset_name) : '',
      asset_type_id: Number(value.asset_type_id),
      asset_status_id: Number(value.asset_status_id),
      project_inspection_template_ids: (
        value.project_inspection_templates || []
      ).map((t: { id: number }) => t.id),
      notes: value.notes || undefined,
      city: value.city || undefined,
      state: value.state || undefined,
      county: value.county || undefined,
      latitude: value.latitude ? Number(value.latitude) : undefined,
      longitude: value.longitude ? Number(value.longitude) : undefined,
      field_answers,
    };

    try {
      if (isEdit) {
        await projectAssetsApi.update(projectId, asset!.id, payload);
      } else {
        await projectAssetsApi.create(projectId, payload);
      }
      toast.success(`Asset ${isEdit ? 'updated' : 'created'}`);
      onSaved();
      onClose();
    } catch (err) {
      // Surface backend validation messages (e.g. duplicate Asset ID).
      const message =
        (err as any)?.response?.data?.message ||
        `Failed to ${isEdit ? 'update' : 'create'} asset. Please try again.`;

      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  // Prefill form values when editing, including saved static-field answers.
  const initialValue = isEdit
    ? {
        asset_name: asset!.asset_name,
        asset_type_id: asset!.asset_type_id,
        asset_status_id: asset!.asset_status_id,
        project_inspection_templates: (asset!.project_inspection_templates || []).map(
          (t) => ({
            id: t.id,
            name: t.inspection_template?.name || `Template ${t.id}`,
          }),
        ),
        city: asset!.city || '',
        state: asset!.state || '',
        county: asset!.county || '',
        latitude: asset!.latitude ?? '',
        longitude: asset!.longitude ?? '',
        notes: asset!.notes || '',
        ...(asset!.field_answers || [])
          .filter((a) => a.asset_field_type_id != null)
          .reduce(
            (acc, a) => ({
              ...acc,
              [`${FIELD_PREFIX}${a.asset_field_type_id}`]: a.value ?? '',
            }),
            {} as Record<string, string>,
          ),
      }
    : { project_inspection_templates: [] };

  return (
    <Modal
      title={isEdit ? `Edit: ${asset!.asset_name}` : 'New Asset'}
      show
      hideButtons
      handleClose={onClose}
      classes='asset-type-modal'
    >
      <FormContextProvider initialValue={initialValue}>
        <FormContext.Consumer>
          {(formContext) => {
            const staticFields = staticFieldsFor(
              formContext.value.asset_type_id,
            );
            const staticSchema = staticFields.reduce(
              (acc, f) => ({
                ...acc,
                [`${FIELD_PREFIX}${f.id}`]: controlForField(f),
              }),
              {} as Record<string, any>,
            );

            return (
              <div className='project-asset-form'>
                <FormSchemaFields
                  schema={coreSchema}
                  formData={formContext.value}
                  onChange={formContext.set}
                />

                {staticFields.length > 0 && (
                  <>
                    <label className='asset-config__label'>Asset Details</label>
                    <FormSchemaFields
                      schema={staticSchema}
                      formData={formContext.value}
                      onChange={formContext.set}
                    />
                  </>
                )}

                <div className='asset-config__location-head'>
                  <label className='asset-config__label'>Location & Notes</label>
                  <button
                    type='button'
                    className='tiny outline'
                    onClick={() =>
                      getCurrentPosition()
                        .then(({ latitude, longitude }) => {
                          formContext.set('latitude', latitude);
                          formContext.set('longitude', longitude);
                        })
                        .catch(() => {})
                    }
                  >
                    Capture Location
                  </button>
                </div>
                <FormSchemaFields
                  schema={locationSchema}
                  formData={formContext.value}
                  onChange={formContext.set}
                />

                <div className='asset-type-modal__footer'>
                  <span className='spacer' />
                  <button className='outline' onClick={onClose}>
                    Cancel
                  </button>
                  <button
                    className='primary'
                    disabled={saving || !formContext.isValid}
                    onClick={() => handleSave(formContext.value)}
                  >
                    {saving
                      ? 'Saving…'
                      : isEdit
                      ? 'Save Changes'
                      : 'Create Asset'}
                  </button>
                </div>
              </div>
            );
          }}
        </FormContext.Consumer>
      </FormContextProvider>
    </Modal>
  );
};

export default CreateAssetModal;
